import { useGSAP } from "@gsap/react"
import { useGLTF } from "@react-three/drei"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useRef } from "react"
import type { Group } from "three"

gsap.registerPlugin(ScrollTrigger, useGSAP)

const MODEL_URL = "/robot.glb"
const FACE_CAMERA_Y = -Math.PI / 2

const HERO_SCALE = 1.4
const END_SCALE = 1.5
const MOBILE_HERO_SCALE = 0.85
const MOBILE_QUERY = "(max-width: 767px)"

type Pose = {
  trigger: string
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  scale: number
}

const POSES: Array<Pose> = [
  {
    trigger: "#about",
    position: { x: 1.6, y: -1.1, z: 0 },
    rotation: { x: 0.08, y: FACE_CAMERA_Y - Math.PI * 0.25, z: 0 },
    scale: END_SCALE,
  },
  {
    trigger: "#cast",
    position: { x: -1.9, y: -1.3, z: 0 },
    rotation: { x: 0.06, y: FACE_CAMERA_Y + Math.PI * 0.2, z: -0.1 },
    scale: 1.2,
  },
  {
    trigger: "#company",
    position: { x: 1.8, y: -1.1, z: 0 },
    rotation: { x: 0.04, y: FACE_CAMERA_Y - Math.PI * 0.32, z: 0.08 },
    scale: 1.2,
  },
  {
    trigger: "#creator",
    position: { x: -1.8, y: -1.1, z: 0 },
    rotation: { x: 0.04, y: FACE_CAMERA_Y + Math.PI * 0.18, z: -0.08 },
    scale: 1.2,
  },
  {
    trigger: "#outro",
    position: { x: 1.4, y: -0.7, z: 0 },
    rotation: { x: 0.04, y: FACE_CAMERA_Y - Math.PI * 0.2, z: 0.05 },
    scale: 1.2,
  },
]

const MOBILE_POSES: Array<Pose> = [
  {
    trigger: "#about",
    position: { x: 0.55, y: -1.85, z: 0 },
    rotation: { x: 0.06, y: FACE_CAMERA_Y - Math.PI * 0.18, z: 0 },
    scale: 0.8,
  },
  {
    trigger: "#cast",
    position: { x: -0.65, y: -1.75, z: 0 },
    rotation: { x: 0.05, y: FACE_CAMERA_Y + Math.PI * 0.18, z: -0.06 },
    scale: 0.75,
  },
  {
    trigger: "#company",
    position: { x: 0.6, y: -1.0, z: 0 },
    rotation: { x: 0.04, y: -1.99, z: 0.01 },
    scale: 0.7,
  },
  {
    trigger: "#creator",
    position: { x: -0.65, y: -1.75, z: 0 },
    rotation: { x: 0.04, y: FACE_CAMERA_Y + Math.PI * 0.14, z: -0.05 },
    scale: 0.75,
  },
  {
    trigger: "#outro",
    position: { x: 0, y: -1.55, z: 0 },
    rotation: { x: 0.04, y: FACE_CAMERA_Y, z: 0 },
    scale: 0.85,
  },
]

export function Robot() {
  const group = useRef<Group>(null)
  const { scene } = useGLTF(MODEL_URL)

  useGSAP(() => {
    const obj = group.current
    if (!obj) return

    const isMobile =
      typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches

    const initialY = isMobile ? -1.5 : -2.2
    const initialScale = isMobile ? MOBILE_HERO_SCALE : HERO_SCALE
    const poses = isMobile ? MOBILE_POSES : POSES

    gsap.set(obj.position, { x: 0, y: initialY, z: 0 })
    gsap.set(obj.rotation, { x: 0, y: FACE_CAMERA_Y, z: 0 })
    gsap.set(obj.scale, { x: initialScale, y: initialScale, z: initialScale })

    const PINNED_SECTIONS = new Set([
      "#about",
      "#cast",
      "#company",
      "#creator",
      "#outro",
    ])

    poses.forEach((pose) => {
      const isPinnedSection = PINNED_SECTIONS.has(pose.trigger)
      const tl = gsap.timeline({
        defaults: { ease: "none", immediateRender: false },
        scrollTrigger: {
          trigger: pose.trigger,
          start: isPinnedSection ? "top bottom" : "top 85%",
          end: isPinnedSection ? "top top" : "top 35%",
          scrub: 0.6,
          invalidateOnRefresh: true,
        },
      })
      tl.to(obj.position, pose.position, 0)
        .to(obj.rotation, pose.rotation, 0)
        .to(obj.scale, { x: pose.scale, y: pose.scale, z: pose.scale }, 0)
    })
  })

  return <primitive ref={group} object={scene} />
}

useGLTF.preload(MODEL_URL)
